import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { isGlobalAdmin } from '@/lib/workspaces';
import RoleManagerDialog from '@/components/admin/RoleManagerDialog';
import { Settings, Tag, ShieldCheck } from 'lucide-react';

const SystemSettings = () => {
  const { token, user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const admin = isGlobalAdmin(user);
  const [roles, setRoles] = useState([]);
  const [mgr, setMgr] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/personnel/roles`, auth).then((r) => setRoles(r.data || [])).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const active = roles.filter((r) => r.is_active);
  const inactive = roles.filter((r) => !r.is_active);

  return (
    <div data-testid="system-settings">
      <div className="mb-8">
        <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">System Settings</div>
        <h1 className="mt-1.5 flex items-center gap-2 text-[30px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">
          <Settings size={26} /> Company-Wide Settings
        </h1>
        <p className="mt-2 max-w-2xl text-base text-slate-500">
          Verified company-wide configuration managed by the Global Administrator. This does not include Users &amp; Capabilities,
          Module Directory, customer or personnel data, Payroll or Contract Budget settings.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-roles-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-lg font-bold text-[#0E2A47]"><Tag size={20} /> Primary Operational Roles</div>
            <p className="mt-1.5 max-w-xl text-sm text-slate-500">
              The shared, controlled role list used across Personnel Master. Add, rename, deactivate or reactivate roles.
              Roles in use are deactivated (kept in history), never deleted.
            </p>
          </div>
          {admin ? (
            <button onClick={() => setMgr(true)} data-testid="settings-manage-roles"
              className="shrink-0 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">Manage Roles</button>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500"><ShieldCheck size={13} /> Global Admin only</span>
          )}
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="settings-roles-list">
          {active.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1.5 rounded-full bg-[#8FC93A]/12 px-3 py-1 text-sm font-medium text-[#0E2A47] border border-[#8FC93A]/30">
              {r.name}{r.assigned_count > 0 && <span className="text-[10px] text-slate-400">· {r.assigned_count}</span>}
            </span>
          ))}
          {inactive.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-400 line-through">{r.name}</span>
          ))}
          {roles.length === 0 && <span className="text-sm text-slate-400">Loading roles…</span>}
        </div>
      </div>

      {mgr && <RoleManagerDialog open={mgr} onClose={() => setMgr(false)} onChanged={load} />}
    </div>
  );
};

export default SystemSettings;
